import React, { useState, useEffect } from 'react';
import { FreightForm } from './components/FreightForm';
import { ResultsDisplay } from './components/ResultsDisplay';
import { AIBroker } from './components/AIBroker';
import { LanguageSelector } from './components/LanguageSelector';
import { FreightItem, CalculationResult } from './types';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import GlobalFooter from './components/GlobalFooter';

// Strict NMFC Class calculation based on Standard Density Guidelines
// Source: National Motor Freight Classification (NMFC) standard density scale
const getClassFromDensity = (density: number): string => {
  if (density <= 0) return '0'; // Invalid
  if (density < 1) return '400';
  if (density < 2) return '300';
  if (density < 4) return '250';
  if (density < 6) return '150';
  if (density < 8) return '125';
  if (density < 10) return '100';
  if (density < 12) return '92.5';
  if (density < 15) return '85';
  if (density < 22.5) return '70';
  if (density < 30) return '65';
  if (density < 35) return '60';
  if (density < 50) return '55';
  return '50'; // 50 pcf or greater
};

const CalculatorContent: React.FC = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState<FreightItem[]>([
    { id: '1', length: 48, width: 40, height: 48, weight: 800, quantity: 1, isStackable: false, description: '' }
  ]);
  
  const [result, setResult] = useState<CalculationResult>({
    totalWeight: 0,
    totalCubicFeet: 0,
    density: 0,
    estimatedClass: '0',
    linearFeet: 0
  });

  useEffect(() => {
    // Independent Calculation Logic
    let totalWeight = 0;
    let totalCubicFeet = 0;
    let totalLinearFeet = 0;

    // We need to calculate linear feet based on how items fit in a standard trailer (approx 96-102" wide).
    // Standard logic: 
    // 1. If width > 48-50", it takes a full "spot" (cannot be side-by-side).
    // 2. If width <= 48-50", it can be side-by-side (2 per row).
    // 3. If stackable, we can vertically stack them.
    
    // To simplify accurately: We calculate "Floor Spots".
    // A standard pallet (48x40) takes 4ft of length if placed 48" way, or 3.33ft if placed 40" way.
    // LTL carriers usually bill based on the "lenght of the trailer used".
    
    let currentRowLength = 0;
    let floorSpots: { length: number; stackable: boolean }[] = [];

    items.forEach(item => {
      // Input Validation
      const l = Math.max(0, item.length);
      const w = Math.max(0, item.width);
      const h = Math.max(0, item.height);
      const weight = Math.max(0, item.weight);
      const qty = Math.max(0, item.quantity);

      totalWeight += weight * qty;
      
      // Calculate cubic feet: (L x W x H) / 1728 * Qty
      const itemVol = (l * w * h) / 1728;
      totalCubicFeet += itemVol * qty;

      // Linear Feet Logic:
      // Assume orientation ensures widest dimension is Width for calculation if > length? 
      // Actually standard practice is strict input dimensions.
      
      for(let i = 0; i < qty; i++) {
         // Determine if item occupies full width or half width
         // Threshold is usually ~50 inches. If > 50, it takes the full width line.
         // If <= 50, it takes half width.
         const takesFullWidth = w > 50; 
         
         // In a simple linear calc, we sum the linear feet.
         // If takes full width: Linear Feet += Length
         // If takes half width: Linear Feet += Length / 2
         
         let effectiveLength = l / 12; // convert inches to feet
         
         if (!takesFullWidth) {
             effectiveLength = effectiveLength / 2;
         }
         
         // Stackability logic:
         // If it is stackable, we assume it can be stacked ON TOP of another similar item.
         // So effectively, we divide the linear footprint by 2 again?
         // CAUTION: Stackability depends on total height. Trailer height is ~96-110".
         // If 2 * height > 96, it cannot stack regardless of the flag.
         
         const canPhysicallyStack = (h * 2) <= 96;
         
         if (item.isStackable && canPhysicallyStack) {
             effectiveLength = effectiveLength / 2;
         }

         totalLinearFeet += effectiveLength;
      }
    });

    const density = totalCubicFeet > 0 ? totalWeight / totalCubicFeet : 0;
    const estimatedClass = totalCubicFeet > 0 ? getClassFromDensity(density) : '-';

    setResult({
      totalWeight,
      totalCubicFeet,
      density,
      estimatedClass,
      linearFeet: totalLinearFeet
    });
  }, [items]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50 shadow-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Logo Icon */}
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center shadow-lg shadow-indigo-500/30 text-white">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
                </svg>
            </div>
            <div>
                <h1 className="text-2xl font-bold text-white tracking-tight leading-none">{t('appTitle')}</h1>
                <p className="text-[10px] text-slate-400 font-medium tracking-wider uppercase mt-0.5">{t('appSubtitle')}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
             {/* Language Selector */}
             <LanguageSelector />
             
             <div className="hidden md:flex items-center gap-4 text-sm font-medium text-slate-400">
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-800 border border-slate-700">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    {t('systemOp')}
                </span>
             </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full space-y-10">
        
        {/* Hero / Intro */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 pb-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">{t('heroTitle')}</h2>
            <p className="mt-3 text-lg text-slate-600 leading-relaxed">
               {t('heroDesc')}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Input Form */}
          <div className="lg:col-span-7 space-y-8">
            <FreightForm items={items} setItems={setItems} />
          </div>

          {/* Right Column: Results sticky on desktop */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
            <ResultsDisplay result={result} />
            
            {/* Static Disclaimer Box */}
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 text-sm text-amber-900/80 flex gap-3 shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-500">
                <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" />
              </svg>
              <div>
                <strong className="block text-amber-900 mb-1">{t('disclaimerTitle')}</strong>
                {t('disclaimerText')}
              </div>
            </div>
          </div>
        </div>

        {/* AI Section */}
        <AIBroker items={items} result={result} />

      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-12 py-12 no-print">
         <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
                <p className="text-sm text-slate-500 font-medium">{t('appTitle')} ©</p>
                <p className="text-xs text-slate-400 mt-1">{t('footerRights')}</p>
            </div>
            
            <div className="flex flex-col items-center md:items-end gap-2">
                 <a href="mailto:service@promptvault.work" className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors">
                    service@promptvault.work
                 </a>
                 <span className="text-xs text-slate-400">
                    {t('footerSupport')}
                 </span>
            </div>
         </div>
         <div className="max-w-6xl mx-auto px-4 mt-8 pt-8 border-t border-slate-100 text-center">
            <p className="text-[10px] text-slate-400 uppercase tracking-widest">
                {t('footerLegal')}
            </p>
         </div>
      </footer>

      {/* Global Network Footer */}
      <GlobalFooter />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <CalculatorContent />
    </LanguageProvider>
  );
};

export default App;